import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FileText, Plus, X, CheckCircle, AlertCircle, QrCode, Mail, Search, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ConsentCollectionModal from '@/components/ConsentCollectionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONSENT_PURPOSES = [
  { id: 'newsletter', label: 'Newsletter', description: 'Receber newsletter com novidades e conteúdos' },
  { id: 'promotions', label: 'Promoções', description: 'Receber ofertas e promoções exclusivas' },
  { id: 'events', label: 'Eventos', description: 'Convites para eventos e webinars' },
  { id: 'surveys', label: 'Inquéritos', description: 'Participar em inquéritos de satisfação' },
  { id: 'product_updates', label: 'Atualizações de Produto', description: 'Novidades sobre produtos e serviços' }
];

const CONTACT_TYPES = [
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Telefone' },
  { id: 'sms', label: 'SMS' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'postal', label: 'Correio Postal' }
];

const ConsentsPage = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showQRValidation, setShowQRValidation] = useState(false);
  
  // Titular search state
  const [titulares, setTitulares] = useState([]);
  const [titularSearchTerm, setTitularSearchTerm] = useState('');
  const [selectedTitular, setSelectedTitular] = useState(null);
  const [showTitularSearch, setShowTitularSearch] = useState(false);
  const [loadingTitulares, setLoadingTitulares] = useState(false);
  
  const [newConsent, setNewConsent] = useState({
    contact_type: '',
    contact_value: '',
    purposes: [],
    validation_method: 'qr_code'
  });

  useEffect(() => {
    fetchConsents();
  }, []);

  // Fetch titulares for search
  const fetchTitulares = async (search = '') => {
    setLoadingTitulares(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/titulares`, { withCredentials: true });
      const allTitulares = response.data.titulares || response.data || [];
      
      // Filter by search term if provided
      const filtered = search 
        ? allTitulares.filter(t => 
            t.name?.toLowerCase().includes(search.toLowerCase()) ||
            t.email?.toLowerCase().includes(search.toLowerCase()) ||
            t.nif?.includes(search)
          )
        : allTitulares;
      
      setTitulares(filtered);
    } catch (error) {
      console.error('Error fetching titulares:', error);
    } finally {
      setLoadingTitulares(false);
    }
  };

  const fetchConsents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/consents`, { withCredentials: true });
      setConsents(response.data);
    } catch (error) {
      console.error('Error fetching consents:', error);
      toast.error('Erro ao carregar consentimentos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConsent = async () => {
    if (!selectedTitular) {
      toast.error('Por favor selecione um titular');
      return;
    }
    
    if (!newConsent.contact_type || !newConsent.contact_value || newConsent.purposes.length === 0) {
      toast.error('Por favor preencha todos os campos obrigatórios');
      return;
    }
    
    // If QR Code validation, open QR modal instead of creating directly
    if (newConsent.validation_method === 'qr_code') {
      setIsDialogOpen(false);
      setShowQRValidation(true);
      return;
    }
    
    // For OTP validation, create consent directly (OTP will be sent)
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/consents`, {
        ...newConsent,
        titular_id: selectedTitular.user_id
      }, { withCredentials: true });
      toast.success('Consentimento criado com sucesso!');
      setIsDialogOpen(false);
      resetConsentForm();
      fetchConsents();
    } catch (error) {
      console.error('Error creating consent:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar consentimento');
    } finally {
      setSubmitting(false);
    }
  };

  const resetConsentForm = () => {
    setNewConsent({
      contact_type: '',
      contact_value: '',
      purposes: [],
      validation_method: 'qr_code'
    });
    setSelectedTitular(null);
    setTitularSearchTerm('');
    setShowTitularSearch(false);
  };

  const handleOpenCreateDialog = () => {
    resetConsentForm();
    fetchTitulares('');
    setShowTitularSearch(true);
    setIsDialogOpen(true);
  };

  const handleSelectTitular = (titular) => {
    setSelectedTitular(titular);
    setShowTitularSearch(false);
    // Pre-fill contact info
    setNewConsent(prev => ({
      ...prev,
      contact_type: 'email',
      contact_value: titular.email || ''
    }));
  };

  const handleQRValidationComplete = (consent) => {
    toast.success('Consentimento validado e guardado com sucesso!');
    setShowQRValidation(false);
    resetConsentForm();
    fetchConsents();
  };

  const handleRevokeConsent = async (consentId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/consents/${consentId}`, { withCredentials: true });
      toast.success('Consentimento revogado com sucesso');
      fetchConsents();
    } catch (error) {
      console.error('Error revoking consent:', error);
      toast.error('Erro ao revogar consentimento');
    }
  };

  const handlePurposeChange = (purposeId, checked) => {
    if (checked) {
      setNewConsent(prev => ({
        ...prev,
        purposes: [...prev.purposes, purposeId]
      }));
    } else {
      setNewConsent(prev => ({
        ...prev,
        purposes: prev.purposes.filter(p => p !== purposeId)
      }));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      revoked: 'bg-red-50 text-red-700 border-red-200'
    };
    
    const labels = {
      active: 'Ativo',
      pending: 'Pendente',
      revoked: 'Revogado'
    };
    
    const icons = {
      active: <CheckCircle className="w-3 h-3 mr-1" />,
      pending: <AlertCircle className="w-3 h-3 mr-1" />,
      revoked: <X className="w-3 h-3 mr-1" />
    };
    
    return (
      <Badge className={`${styles[status] || styles.pending} flex items-center`}>
        {icons[status]}
        {labels[status] || status}
      </Badge>
    );
  };

  const getPurposeLabel = (purposeId) => {
    const purpose = CONSENT_PURPOSES.find(p => p.id === purposeId);
    return purpose ? purpose.label : purposeId;
  };

  const getContactTypeLabel = (typeId) => {
    const type = CONTACT_TYPES.find(t => t.id === typeId);
    return type ? type.label : typeId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="consents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestão de Consentimentos
          </h1>
          <p className="text-slate-600">
            {user?.role === 'titular' 
              ? 'Gira os seus consentimentos de marketing e comunicação'
              : 'Visualizar e gerir todos os consentimentos de marketing'}
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            data-testid="qr-code-action-button"
            variant="outline"
            className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white"
            onClick={() => navigate('/qr-code')}
          >
            <QrCode className="w-4 h-4 mr-2" />
            QR Code
          </Button>
          <Button
            data-testid="otp-validation-action-button"
            variant="outline"
            className="border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"
            onClick={() => navigate('/otp-validation')}
          >
            <Mail className="w-4 h-4 mr-2" />
            Validação OTP
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-consent-button"
                className="bg-[#10B981] hover:bg-[#10B981]/90 text-white"
                onClick={handleOpenCreateDialog}
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Consentimento
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#0F172A]">Criar Novo Consentimento</DialogTitle>
              <DialogDescription>
                Selecione o titular e preencha os dados para registar um novo consentimento.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Titular Selection */}
              <div className="space-y-2">
                <Label>Titular dos Dados *</Label>
                {selectedTitular ? (
                  <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-emerald-800">{selectedTitular.name}</p>
                        <p className="text-sm text-emerald-600">{selectedTitular.email}</p>
                        {selectedTitular.nif && <p className="text-xs text-emerald-500">NIF: {selectedTitular.nif}</p>}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => { setSelectedTitular(null); setShowTitularSearch(true); }}
                        className="text-emerald-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Pesquisar titular por nome, email ou NIF..."
                        value={titularSearchTerm}
                        onChange={(e) => {
                          setTitularSearchTerm(e.target.value);
                          fetchTitulares(e.target.value);
                        }}
                        className="pl-10"
                        data-testid="titular-search-input"
                      />
                    </div>
                    {showTitularSearch && (
                      <div className="border rounded-lg max-h-48 overflow-y-auto">
                        {loadingTitulares ? (
                          <div className="p-4 text-center text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                            A carregar...
                          </div>
                        ) : titulares.length > 0 ? (
                          <div className="divide-y">
                            {titulares.map(t => (
                              <div 
                                key={t.user_id} 
                                className="p-3 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                                onClick={() => handleSelectTitular(t)}
                              >
                                <div>
                                  <p className="font-medium text-sm">{t.name}</p>
                                  <p className="text-xs text-gray-500">{t.email}</p>
                                </div>
                                {t.legal_basis && (
                                  <Badge variant="outline" className="text-xs">
                                    {t.legal_basis === 'consent' ? 'Consentimento' : t.legal_basis}
                                  </Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            Nenhum titular encontrado
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedTitular && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="contact_type">Tipo de Contacto *</Label>
                    <Select 
                      value={newConsent.contact_type} 
                      onValueChange={(value) => setNewConsent(prev => ({ ...prev, contact_type: value }))}
                    >
                      <SelectTrigger data-testid="contact-type-select">
                        <SelectValue placeholder="Selecione o tipo de contacto" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_TYPES.map(type => (
                          <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contact_value">Valor do Contacto *</Label>
                    <Input
                      id="contact_value"
                      data-testid="contact-value-input"
                      placeholder={newConsent.contact_type === 'email' ? 'exemplo@email.com' : 'Ex: +351 912 345 678'}
                      value={newConsent.contact_value}
                      onChange={(e) => setNewConsent(prev => ({ ...prev, contact_value: e.target.value }))}
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Label>Finalidades do Consentimento *</Label>
                    <div className="space-y-3 border rounded-lg p-4 bg-slate-50 max-h-40 overflow-y-auto">
                      {CONSENT_PURPOSES.map(purpose => (
                        <div key={purpose.id} className="flex items-start space-x-3">
                          <Checkbox
                            id={purpose.id}
                            data-testid={`purpose-${purpose.id}`}
                            checked={newConsent.purposes.includes(purpose.id)}
                            onCheckedChange={(checked) => handlePurposeChange(purpose.id, checked)}
                          />
                          <div className="grid gap-1 leading-none">
                            <label htmlFor={purpose.id} className="text-sm font-medium cursor-pointer">
                              {purpose.label}
                            </label>
                            <p className="text-xs text-slate-500">{purpose.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>Método de Validação *</Label>
                    <RadioGroup 
                      value={newConsent.validation_method} 
                      onValueChange={(value) => setNewConsent(prev => ({ ...prev, validation_method: value }))}
                  className="grid grid-cols-2 gap-4"
                >
                  <div className="flex items-center space-x-2 border rounded-lg p-4 cursor-pointer hover:bg-slate-50">
                    <RadioGroupItem value="qr_code" id="qr_code" />
                    <Label htmlFor="qr_code" className="cursor-pointer flex items-center gap-2">
                      <QrCode className="w-4 h-4 text-[#10B981]" />
                      QR Code
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 border rounded-lg p-4 cursor-pointer hover:bg-slate-50">
                    <RadioGroupItem value="otp" id="otp" />
                    <Label htmlFor="otp" className="cursor-pointer flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#3B82F6]" />
                      OTP (Email)
                    </Label>
                  </div>
                </RadioGroup>
                <p className="text-xs text-slate-500">
                  {newConsent.validation_method === 'qr_code' 
                    ? 'O cliente irá validar lendo o QR Code com o telemóvel'
                    : 'Será enviado um código OTP para o email do cliente'}
                </p>
              </div>
              </>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                data-testid="submit-consent-button"
                onClick={handleCreateConsent}
                disabled={submitting || !selectedTitular}
                className="bg-[#10B981] hover:bg-[#10B981]/90"
              >
                {submitting ? 'A criar...' : 'Criar Consentimento'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total de Consentimentos</p>
                <p className="text-3xl font-bold text-[#0F172A]">{consents.length}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Consentimentos Ativos</p>
                <p className="text-3xl font-bold text-emerald-600">
                  {consents.filter(c => c.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Consentimentos Revogados</p>
                <p className="text-3xl font-bold text-red-600">
                  {consents.filter(c => c.status === 'revoked').length}
                </p>
              </div>
              <X className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Consents List */}
      <div className="grid grid-cols-1 gap-4">
        {consents.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Nenhum consentimento encontrado</h3>
              <p className="text-slate-600 mb-4">
                {user?.role === 'titular' 
                  ? 'Clique no botão "Novo Consentimento" para registar as suas preferências de comunicação.'
                  : 'Não existem consentimentos registados para este utilizador.'}
              </p>
              <Button 
                onClick={() => setIsDialogOpen(true)}
                className="bg-[#10B981] hover:bg-[#10B981]/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Consentimento
              </Button>
            </CardContent>
          </Card>
        ) : (
          consents.map((consent) => (
            <Card key={consent.consent_id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`consent-card-${consent.consent_id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {getContactTypeLabel(consent.contact_type)}
                      {getStatusBadge(consent.status)}
                    </CardTitle>
                    <CardDescription className="mt-1 text-base">{consent.contact_value}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-[#0F172A] mb-2">Finalidades:</h4>
                    <div className="flex flex-wrap gap-2">
                      {consent.purposes.map((purpose) => (
                        <Badge key={purpose} variant="outline" className="bg-slate-50">
                          {getPurposeLabel(purpose)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-600">Método de validação:</span>
                      <p className="font-medium text-[#0F172A] capitalize">{consent.validation_method}</p>
                    </div>
                    <div>
                      <span className="text-slate-600">Data de validação:</span>
                      <p className="font-medium text-[#0F172A]">
                        {consent.validation_timestamp 
                          ? new Date(consent.validation_timestamp).toLocaleString('pt-PT')
                          : 'Pendente validação'}
                      </p>
                    </div>
                  </div>

                  {consent.status === 'active' && (
                    <div className="pt-4 border-t border-slate-200 flex justify-end">
                      <Button
                        data-testid={`revoke-consent-${consent.consent_id}`}
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRevokeConsent(consent.consent_id)}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Revogar Consentimento
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Unified Consent Collection Modal */}
      <ConsentCollectionModal
        isOpen={showQRValidation}
        onClose={() => setShowQRValidation(false)}
        titularData={selectedTitular ? {
          user_id: selectedTitular.user_id,
          titular_id: selectedTitular.user_id,
          contact_type: newConsent.contact_type,
          contact_value: newConsent.contact_value,
          email: selectedTitular.email || newConsent.contact_value,
          phone: selectedTitular.phone,
          mobile: selectedTitular.mobile,
          contacts: selectedTitular.contacts,
          purposes: newConsent.purposes
        } : {
          contact_type: newConsent.contact_type,
          contact_value: newConsent.contact_value,
          email: newConsent.contact_value,
          purposes: newConsent.purposes
        }}
        onSuccess={handleQRValidationComplete}
        showForm={false}
      />
    </div>
  );
};

export default ConsentsPage;
